import React, { useState, useEffect } from "react";
import { 
  FaCalendarAlt, FaTools, FaChartBar, FaCar, FaShoppingCart, FaEnvelope,
  FaFileInvoiceDollar, FaRocket, FaCheck, FaArrowRight, FaPlay, FaSearch
} from "react-icons/fa";
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SignInButton } from "@clerk/clerk-react";
import revvlyLogo from "@/img/revvly.png";
import { usePlanes } from "../../hooks/usePlanes";
import { getTrialConfig } from "../../services/configuracionGlobalService";
import SEOHead from "../common/SEOHead";

// Screenshots desde public
const calendarioCitasImg = "/img/screenshots/calendario-citas.png";
const formMantenimientoImg = "/img/screenshots/form-mantenimiento.png";
const pdfGarantiaImg = "/img/screenshots/pdf-garantia.png";
const dashboardImg = "/img/screenshots/dashboard.png";

const LandingB2B = () => {
  const [isVisible, setIsVisible] = useState({});
  const [particles, setParticles] = useState([]);
  const [gears, setGears] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('mensual');
  const [trialDays, setTrialDays] = useState(30);
  
  // Obtener planes desde la base de datos
  const { planes, loading: planesLoading, error: planesError } = usePlanes();

  // Función para manejar login con tipo de usuario
  const handleTallerLogin = () => {
    // Guardar tipo de usuario en localStorage
    localStorage.setItem('revvly_user_type', 'taller');
  };

  // Cargar configuración de trial
  useEffect(() => {
    const loadTrialConfig = async () => {
      try {
        const config = await getTrialConfig();
        setTrialDays(config.trialDays);
      } catch (error) {
        console.error('Error cargando configuración de trial:', error);
        // Mantener valor por defecto de 30 días
      }
    };
    
    loadTrialConfig();
  }, []);

  // Inicializar engranajes con movimiento y rebote - optimizado para móviles
  useEffect(() => {
    // Detectar si es móvil
    const isMobile = window.innerWidth < 768;
    
    // Reducir cantidad y tamaño en móviles
    const gearCount = isMobile ? 2 : 4;
    const maxSize = isMobile ? 80 : 160;
    const minSize = isMobile ? 60 : 96;
    
    const initialGears = [];
    
    for (let i = 0; i < gearCount; i++) {
      const size = minSize + Math.random() * (maxSize - minSize);
      initialGears.push({
        id: i + 1,
        x: Math.random() * (window.innerWidth - size), 
        y: Math.random() * (window.innerHeight - size), 
        vx: (Math.random() - 0.5) * (isMobile ? 0.8 : 1.5), 
        vy: (Math.random() - 0.5) * (isMobile ? 0.8 : 1.5), 
        size: size, 
        color: ['text-blue-400', 'text-purple-400', 'text-cyan-400', 'text-red-400'][i % 4], 
        opacity: isMobile ? 5 : 8,
        spinDirection: i % 2 === 0 ? 1 : -1 
      });
    }
    
    setGears(initialGears);
  }, []);

  // Animación de engranajes con movimiento y rebote
  useEffect(() => {
    const animateGears = () => {
      setGears(prevGears => 
        prevGears.map(gear => {
          let newX = gear.x + gear.vx;
          let newY = gear.y + gear.vy;
          let newVx = gear.vx;
          let newVy = gear.vy;

          // Rebote en bordes horizontales
          if (newX <= 0 || newX >= window.innerWidth - gear.size) {
            newVx = -newVx;
            newX = newX <= 0 ? 0 : window.innerWidth - gear.size;
          }

          // Rebote en bordes verticales
          if (newY <= 0 || newY >= window.innerHeight - gear.size) {
            newVy = -newVy;
            newY = newY <= 0 ? 0 : window.innerHeight - gear.size;
          }

          return {
            ...gear,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy
          };
        })
      );
    };

    // Usar requestAnimationFrame para mejor rendimiento
    let animationId;
    let lastTime = 0;
    const targetFPS = window.innerWidth < 768 ? 30 : 60; // 30fps en móvil, 60fps en desktop
    const frameInterval = 1000 / targetFPS;
    
    const animate = (currentTime) => {
      if (currentTime - lastTime >= frameInterval) {
        animateGears();
        lastTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Función para scroll suave
  const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // Inicializar partículas con rebote - optimizado para móviles
  useEffect(() => {
    // Detectar si es móvil
    const isMobile = window.innerWidth < 768;
    
    // Reducir cantidad en móviles
    const particleCount = isMobile ? 3 : 6;
    const speed = isMobile ? 1 : 2;
    
    const initialParticles = [];
    const colors = ['bg-blue-400', 'bg-purple-400', 'bg-cyan-400', 'bg-red-400', 'bg-yellow-400', 'bg-green-400'];
    
    for (let i = 0; i < particleCount; i++) {
      initialParticles.push({
        id: i + 1,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        size: isMobile ? 2 : 3 + Math.random() * 2,
        color: colors[i % colors.length]
      });
    }
    
    setParticles(initialParticles);
  }, []);

  // Animación de partículas con rebote
  useEffect(() => {
    const animateParticles = () => {
      setParticles(prevParticles => 
        prevParticles.map(particle => {
          let newX = particle.x + particle.vx;
          let newY = particle.y + particle.vy;
          let newVx = particle.vx;
          let newVy = particle.vy;

          // Rebote en bordes horizontales
          if (newX <= 0 || newX >= window.innerWidth - particle.size * 4) {
            newVx = -newVx;
            newX = newX <= 0 ? 0 : window.innerWidth - particle.size * 4;
          }

          // Rebote en bordes verticales
          if (newY <= 0 || newY >= window.innerHeight - particle.size * 4) {
            newVy = -newVy;
            newY = newY <= 0 ? 0 : window.innerHeight - particle.size * 4;
          }

          return {
            ...particle,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy
          };
        })
      );
    };

    // Usar requestAnimationFrame para mejor rendimiento
    let animationId;
    let lastTime = 0;
    const targetFPS = window.innerWidth < 768 ? 30 : 60; // 30fps en móvil, 60fps en desktop
    const frameInterval = 1000 / targetFPS;
    
    const animate = (currentTime) => {
      if (currentTime - lastTime >= frameInterval) {
        animateParticles();
        lastTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Animación de entrada progresiva
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible({ hero: true });
    }, 100);

    const timer2 = setTimeout(() => {
      setIsVisible(prev => ({ ...prev, features: true }));
    }, 300);

    const timer3 = setTimeout(() => {
      setIsVisible(prev => ({ ...prev, plans: true }));
    }, 500);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Features principales - 8 características individuales
  const features = [
    {
      icon: <FaCalendarAlt className="text-4xl" />,
      title: "Calendario y Horarios Inteligentes",
      description: "Sistema completo de agendamiento con control detallado de horarios por sucursal, festivos, días especiales y gestión de clientes simultáneos.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <FaTools className="text-4xl" />,
      title: "Gestión de Mantenimiento e Inventario",
      description: "Gestión completa de servicios y mantenimientos con control de inventario integrado y seguimiento de stock de repuestos en tiempo real.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <FaFileInvoiceDollar className="text-4xl" />,
      title: "PDF de Garantía Profesional",
      description: "Genera documentos profesionales de garantía automáticamente con branding personalizado del taller.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: <FaChartBar className="text-4xl" />,
      title: "Dashboard Analítico Avanzado",
      description: "Centro de inteligencia empresarial con múltiples gráficos: citas, ingresos por categoría, ranking de mecánicos, servicios populares y alertas de stock.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <FaCar className="text-4xl" />,
      title: "Catálogo Centralizado de Vehículos",
      description: "Base de datos completa con todas las marcas y modelos homologadas en Chile para evitar errores.",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: <FaShoppingCart className="text-4xl" />,
      title: "Tienda Integrada de Repuestos",
      description: "Compra directa de repuestos importados de alta calidad con descuentos exclusivos al usar revvly.",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <FaEnvelope className="text-4xl" />,
      title: "Recordatorios Automáticos",
      description: "Notificaciones por email y WhatsApp para mantenimientos programados y seguimiento de clientes.",
      color: "from-teal-500 to-cyan-500"
    },
    {
      icon: <FaSearch className="text-4xl" />,
      title: "Búsqueda por Patente",
      description: "Búsqueda instantánea de clientes y vehículos por patente para eliminar errores de digitación.",
      color: "from-amber-500 to-orange-500"
    }
  ];

  // Los planes ahora se obtienen de la base de datos a través del hook usePlanes

  const landingStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Revvly - Software para Talleres Automotrices",
    "description": "La plataforma más completa para modernizar tu taller automotriz. Gestiona citas, clientes, inventario y finanzas desde un solo lugar.",
    "url": "https://revvly.cl",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": [
      {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "CLP",
        "description": `${trialDays} días de prueba gratuita sin tarjeta de crédito`
      }
    ],
    "creator": {
      "@type": "Organization",
      "name": "Revvly",
      "url": "https://revvly.cl",
      "description": "Empresa líder en software para talleres automotrices en Chile"
    },
    "featureList": [
      "Gestión de citas y calendario inteligente",
      "Control de inventario y repuestos",
      "Facturación electrónica",
      "Reportes y analytics avanzados",
      "Gestión multi-sucursal",
      "Catálogo completo de vehículos chilenos",
      "PDF de garantía profesional",
      "Dashboard analítico empresarial"
    ],
    "screenshot": "https://revvly.cl/og-image.jpg",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5"
    }
  };

  return (
    <>
      <SEOHead
        title="Revvly - Software para Talleres Automotrices Chile | Gestión Integral"
        description={`Sistema líder de gestión para talleres mecánicos en Chile. ${trialDays} días gratis, sin tarjeta de crédito. Aumenta tu productividad hasta 300% con nuestro software integral.`}
        keywords="software taller automotriz chile, gestión talleres mecánicos, sistema talleres chile, software mecánico, administración taller, facturación taller, inventario repuestos, citas taller, software automotive chile"
        url="https://revvly.cl"
        structuredData={landingStructuredData}
      />
      
      <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white min-h-screen overflow-hidden">
        {/* Efectos de fondo animados - Engranajes flotantes que se mueven y rebotan */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Engranajes dinámicos con movimiento y rebote */}
        {gears.map(gear => (
          <div 
            key={gear.id}
            className="absolute"
            style={{
              left: `${gear.x}px`,
              top: `${gear.y}px`,
              width: `${gear.size}px`,
              height: `${gear.size}px`,
              opacity: gear.opacity / 100
            }}
          >
            <svg 
              className={`w-full h-full ${gear.color} ${gear.spinDirection === 1 ? 'animate-spin-slow' : 'animate-spin-reverse-slow'}`} 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
            </svg>
          </div>
        ))}

        {/* Partículas con rebote que se mueven por la pantalla */}
        {particles.map(particle => (
          <div
            key={particle.id}
            className="absolute opacity-60 blur-sm transition-all duration-75 ease-linear"
            style={{
              left: `${particle.x}px`,
              top: `${particle.y}px`,
              width: `${particle.size * 4}px`,
              height: `${particle.size * 4}px`,
            }}
          >
            <div className={`w-full h-full ${particle.color} rounded-full relative shadow-lg`}>
              <div className="absolute inset-0 bg-white rounded-full transform scale-25 opacity-40"></div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="relative">
          <div className="absolute inset-0 h-32 bg-gradient-to-b from-black via-gray-900 to-transparent opacity-80"></div>
          <div className="relative container mx-auto px-6 py-8">
            <nav className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <img src={revvlyLogo} alt="Revvly" className="h-10" />
              </div>
              <div className="hidden md:flex space-x-8">
                <button onClick={() => smoothScrollTo('features')} className="hover:text-blue-400 transition-colors duration-300">Características</button>
                <button onClick={() => smoothScrollTo('plans')} className="hover:text-blue-400 transition-colors duration-300">Planes</button>
                <button onClick={() => smoothScrollTo('demo')} className="hover:text-blue-400 transition-colors duration-300">Demo</button>
              </div>
              <SignInButton mode="modal">
                <button 
                  onClick={handleTallerLogin}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  Comienza Gratis
                </button>
              </SignInButton>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className={`container mx-auto px-6 py-20 text-center transition-all duration-1000 ${isVisible.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Revoluciona tu taller automotriz con
              </span>
              <br />
              <div className="flex justify-center items-center mt-4">
                <div className="relative">
                  {/* Resplandor sutil azul en la parte inferior */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-2 w-full h-1/2 bg-blue-400 blur-3xl opacity-40 rounded-full"></div>
                  <img 
                    src={revvlyLogo} 
                    alt="Revvly" 
                    className="relative h-16 md:h-20" 
                    style={{
                      filter: 'brightness(1.4) contrast(1.3) saturate(1.2) drop-shadow(0 0 15px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 30px rgba(255, 255, 255, 0.3))'
                    }}
                  />
                </div>
              </div>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
              La plataforma más completa para modernizar tu taller automotriz. Gestiona citas, clientes, inventario y finanzas desde un solo lugar.
              <span className="text-blue-400 font-semibold"> Optimiza tu negocio y aumenta tus ingresos hasta un 300%</span>
            </p>

            {/* Destacar prueba gratuita y sin medios de pago */}
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-12 text-lg">
              <div className="flex items-center space-x-3 bg-green-500/10 border border-green-500/30 rounded-full px-6 py-3">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold">{trialDays} días de prueba gratuita</span>
              </div>
              <div className="flex items-center space-x-3 bg-blue-500/10 border border-blue-500/30 rounded-full px-6 py-3">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                <span className="text-blue-400 font-semibold">Sin tarjeta de crédito</span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
              <SignInButton mode="modal">
                <button 
                  onClick={handleTallerLogin}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center space-x-2"
                >
                  <FaRocket />
                  <span>Comenzar Gratis</span>
                  <FaArrowRight />
                </button>
              </SignInButton>
              
              <button className="border-2 border-blue-400 hover:bg-blue-400 hover:text-gray-900 px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 transform hover:scale-105 flex items-center space-x-2">
                <FaPlay />
                <span>Ver Demo</span>
              </button>
            </div>

          </div>
        </section>

        {/* Features Overview */}
        <section id="features" className={`py-20 transition-all duration-1000 delay-300 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                  Funcionalidades del Software para Talleres
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Suite completa de herramientas para la administración y gestión eficiente de talleres automotrices
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  onClick={() => smoothScrollTo(`feature-section-${index}`)}
                  className="group relative bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-blue-500 transition-all duration-500 transform hover:scale-105 hover:shadow-xl text-center cursor-pointer"
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                  
                  {/* Indicador de navegación */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <FaArrowRight className="text-blue-400 text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nueva sección para las 4 características adicionales - PRIMERO */}
        <section className="py-20 bg-gray-900/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <span className="bg-gradient-to-r from-cyan-400 to-indigo-600 bg-clip-text text-transparent">
                  Herramientas Avanzadas para Talleres
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Tecnología innovadora que optimiza la gestión y administración de talleres automotrices en Chile
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.slice(4, 8).map((feature, index) => (
                <div 
                  key={`extra-feature-${index}`} 
                  id={`feature-section-${index + 4}`}
                  className={`bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-blue-500 transition-all duration-500 transform hover:scale-105 hover:shadow-xl ${(index === 1 || index === 2) ? 'relative' : ''}`}
                >
                  {/* Badge de "Próximamente" para Tienda y Recordatorios */}
                  {(index === 1 || index === 2) && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        Próximamente
                      </div>
                    </div>
                  )}
                  
                  {/* Desktop layout: lado a lado */}
                  <div className="hidden md:flex items-start space-x-6">
                    <div className={`flex-shrink-0 p-4 rounded-xl bg-gradient-to-br ${feature.color} ${(index === 1 || index === 2) ? 'opacity-75' : ''}`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-2xl font-bold mb-3 ${(index === 1 || index === 2) ? 'text-gray-300' : 'text-white'}`}>
                        {feature.title}
                        {(index === 1 || index === 2) && (
                          <span className="text-sm font-normal text-amber-400 ml-2">(En desarrollo)</span>
                        )}
                      </h3>
                      <p className={`mb-4 ${(index === 1 || index === 2) ? 'text-gray-400' : 'text-gray-300'}`}>{feature.description}</p>
                      
                      <div className="space-y-3">
                        {index === 0 && ( // Catálogo Centralizado
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Base de datos oficial homologada</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Información técnica completa</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Actualización automática</span>
                            </div>
                          </>
                        )}
                        {index === 1 && ( // Tienda de Repuestos - PRÓXIMAMENTE
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Productos importados certificados</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Descuentos exclusivos por volumen</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Integración directa con órdenes</span>
                            </div>
                          </>
                        )}
                        {index === 2 && ( // Recordatorios - PRÓXIMAMENTE
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Email personalizado automático</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">WhatsApp con plantillas</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Programación flexible</span>
                            </div>
                          </>
                        )}
                        {index === 3 && ( // Búsqueda por Patente
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Búsqueda instantánea</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Autocompletado inteligente</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Cero errores de digitación</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile layout: icono y título en una fila, texto abajo */}
                  <div className="md:hidden">
                    {/* Fila superior: icono y título alineados */}
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`flex-shrink-0 p-3 rounded-xl bg-gradient-to-br ${feature.color} ${(index === 1 || index === 2) ? 'opacity-75' : ''}`}>
                        <div className="text-3xl">
                          {feature.icon}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-xl font-bold ${(index === 1 || index === 2) ? 'text-gray-300' : 'text-white'}`}>
                          {feature.title}
                        </h3>
                        {(index === 1 || index === 2) && (
                          <span className="text-xs font-normal text-amber-400">(En desarrollo)</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Descripción ocupando todo el ancho */}
                    <div className="w-full">
                      <p className={`mb-4 ${(index === 1 || index === 2) ? 'text-gray-400' : 'text-gray-300'}`}>{feature.description}</p>
                      
                      <div className="space-y-3">
                        {index === 0 && ( // Catálogo Centralizado
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Base de datos oficial homologada</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Información técnica completa</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Actualización automática</span>
                            </div>
                          </>
                        )}
                        {index === 1 && ( // Tienda de Repuestos - PRÓXIMAMENTE
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Productos importados certificados</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Descuentos exclusivos por volumen</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Integración directa con órdenes</span>
                            </div>
                          </>
                        )}
                        {index === 2 && ( // Recordatorios - PRÓXIMAMENTE
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Email personalizado automático</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">WhatsApp con plantillas</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-amber-400 text-sm" />
                              <span className="text-gray-400 text-sm">Programación flexible</span>
                            </div>
                          </>
                        )}
                        {index === 3 && ( // Búsqueda por Patente
                          <>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Búsqueda instantánea</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Autocompletado inteligente</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <FaCheck className="text-green-400 text-sm" />
                              <span className="text-gray-300 text-sm">Cero errores de digitación</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Individual Feature Sections con imágenes - DESPUÉS */}
        {features.slice(0, 4).map((feature, index) => (
          <section key={`feature-${index}`} id={`feature-section-${index}`} className="py-20">
            <div className="container mx-auto px-6">
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20`}>
                {/* Content Side */}
                <div className="lg:w-1/3 space-y-6">
                  <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                    <span className={`bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                      {feature.title}
                    </span>
                  </h2>
                  
                  <p className="text-xl text-gray-300 leading-relaxed">
                    {index === 3 ? 
                      "Centro de inteligencia empresarial con más de 10 gráficos avanzados: análisis temporal de citas, distribución de estados, ingresos detallados por categoría (servicios, repuestos, IVA), rankings de mecánicos con medallas, servicios más populares, repuestos más usados, comparativas de productividad y alertas automáticas de inventario crítico." 
                      : feature.description
                    }
                  </p>
                  
                  <div className="space-y-4">
                    {index === 0 && (
                      <>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Control de horarios por sucursal</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Gestión de festivos y días especiales</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Configuración de clientes simultáneos</span>
                        </div>
                      </>
                    )}
                    {index === 1 && (
                      <>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Historial completo de servicios</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Control de inventario en tiempo real</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Gestión automática de stock de repuestos</span>
                        </div>
                      </>
                    )}
                    {index === 2 && (
                      <>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Documentos profesionales automáticos</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Branding personalizado del taller</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Envío automático por email</span>
                        </div>
                      </>
                    )}
                    {index === 3 && (
                      <>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">10+ gráficos interactivos (líneas, barras, pie, donut)</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Rankings con medallas 🥇🥈🥉 y comparativas</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaCheck className="text-green-400" />
                          <span className="text-gray-300">Análisis de ingresos por servicios/repuestos/IVA</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <button className={`inline-flex items-center space-x-2 bg-gradient-to-r ${feature.color} hover:shadow-lg px-6 py-3 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105`}>
                    <FaPlay />
                    <span>Ver Demo</span>
                  </button>
                </div>

                {/* Image Side */}
                <div className="lg:w-2/3">
                  <div className="relative">
                    {/* Background glow effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-20 blur-3xl rounded-3xl`}></div>
                    
                    {/* Screenshot real sin marcos del navegador */}
                    <div className="relative bg-gray-800 bg-opacity-60 backdrop-blur-sm rounded-3xl p-3 border border-gray-700 shadow-2xl">
                      <div className="relative overflow-hidden rounded-2xl">
                        <img 
                          src={index === 0 ? calendarioCitasImg : index === 1 ? formMantenimientoImg : index === 2 ? pdfGarantiaImg : dashboardImg} 
                          alt={feature.title}
                          className="w-full h-auto object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Plans Section */}
        <section id="plans" className={`py-20 transition-all duration-1000 delay-500 ${isVisible.plans ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <span className="bg-gradient-to-r from-green-400 to-blue-600 bg-clip-text text-transparent">
                  Planes Flexibles
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Elige el plan perfecto para tu taller. Todos incluyen {trialDays} días de prueba gratuita sin tarjeta de crédito
              </p>
              
              {/* Selector de período */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-800 rounded-lg p-1 flex">
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('mensual')}
                    className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === 'mensual'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Mensual
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('anual')}
                    className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === 'anual'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Anual
                    <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded">
                      -20%
                    </span>
                  </button>
                </div>
              </div>
            </div>

{planesLoading ? (
              // Estado de carga
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Cargando planes...</p>
              </div>
            ) : planesError ? (
              // Estado de error
              <div className="text-center py-12">
                <div className="text-red-400 mb-4">
                  <FaRocket className="mx-auto h-12 w-12 mb-4" />
                  <p className="text-xl font-semibold">Error cargando planes</p>
                  <p className="text-gray-400 mt-2">Por favor, recarga la página</p>
                </div>
              </div>
            ) : planes.length === 0 ? (
              // No hay planes
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <FaRocket className="mx-auto h-12 w-12 mb-4" />
                  <p className="text-xl font-semibold">No hay planes disponibles</p>
                </div>
              </div>
            ) : (
              // Tabla comparativa moderna para desktop
              <div className="hidden lg:block mb-8">
                <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700/50 shadow-2xl">
                  {/* Gradiente decorativo */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 rounded-2xl"></div>
                  
                  {/* Headers de planes */}
                  <div className="relative grid grid-cols-5 gap-6 mb-8">
                    {/* Columna vacía para características */}
                    <div className="flex items-end pb-6">
                      <h3 className="text-xl font-bold text-transparent bg-gradient-to-r from-white to-gray-300 bg-clip-text">
                        Características
                      </h3>
                    </div>
                    
                    {/* Headers de cada plan */}
                    {planes.map((plan) => (
                      <div key={plan.id} className="text-center relative">
                        {/* Badge popular */}
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                            <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                              Más Popular
                            </div>
                          </div>
                        )}
                        
                        
                        {/* Card del plan */}
                        <div className={`
                          relative p-6 rounded-xl border-2 transition-all duration-300 hover:scale-105
                          ${plan.popular 
                            ? 'border-amber-400/50 bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-red-500/20 shadow-lg shadow-amber-500/30' 
                            : 'border-gray-600/50 bg-gradient-to-br from-gray-800/80 to-gray-900/80 hover:border-blue-500/50'
                          }
                        `}>
                          {/* Logo del plan */}
                          <div className={`w-20 h-10 mx-auto mb-3 rounded-lg bg-gradient-to-r ${plan.color || 'from-blue-500 to-blue-600'} flex items-center justify-center shadow-lg`}>
                            <img 
                              src={revvlyLogo} 
                              alt="Revvly" 
                              className="w-16 h-6 object-contain"
                            />
                          </div>
                          
                          {/* Nombre del plan */}
                          <h3 className="text-xl font-bold text-white mb-3">{plan.name}</h3>
                          
                          {/* Precio */}
                          <div className="mb-4">
                            <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-white to-gray-200 bg-clip-text">
                              {selectedPeriod === 'anual' 
                                ? (plan.price === 0 ? 'Gratis' : `$${Math.round(plan.price * 12 * 0.8).toLocaleString('es-CL')}`)
                                : (plan.price === 0 ? 'Gratis' : `$${plan.price.toLocaleString('es-CL')}`)
                              }
                              {plan.price > 0 && (
                                <span className="text-sm text-gray-400 block">
                                  {selectedPeriod === 'anual' ? '/año' : '/mes'}
                                </span>
                              )}
                            </div>
                            {selectedPeriod === 'anual' && plan.price > 0 && (
                              <div className="text-xs text-green-400">
                                Ahorra ${Math.round(plan.price * 12 * 0.2).toLocaleString('es-CL')}
                              </div>
                            )}
                          </div>
                          
                          {/* Badge para plan gratuito - entre precio y descripción */}
                          {plan.price === 0 && (
                            <div className="flex justify-center mb-3">
                              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                Sin tarjeta de crédito
                              </div>
                            </div>
                          )}
                          
                          {/* Descripción */}
                          <p className="text-gray-400 text-sm mb-6 min-h-[40px] flex items-center justify-center text-center">
                            {plan.description}
                          </p>
                          
                          {/* Botón CTA */}
                          <SignInButton mode="modal">
                            <button
                              type="button"
                              onClick={handleTallerLogin}
                              className={`
                                group relative w-full py-3 px-4 rounded-lg font-bold text-sm overflow-hidden transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl
                                ${plan.popular 
                                  ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/40 hover:shadow-orange-500/60'
                                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50'
                                }
                              `}
                            >
                              <span className="relative z-10 flex items-center justify-center">
                                <span className="text-xs">{plan.price === 0 ? 'Comenzar Gratis' : `Prueba ${trialDays} días gratis`}</span>
                                <FaArrowRight className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-1" />
                              </span>
                              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                            </button>
                          </SignInButton>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Filas de comparación */}
                  <div className="relative space-y-3">
                    {/* Sucursales */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Sucursales</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center text-white font-semibold">
                          {plan.limites?.sucursales === -1 ? (
                            <span className="text-green-400">Ilimitadas</span>
                          ) : (
                            plan.limites?.sucursales || '1'
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Usuarios */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Clientes</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center text-white font-semibold">
                          {plan.limites?.usuarios === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            plan.limites?.usuarios || '2'
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Vehículos */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Vehículos</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center text-white font-semibold">
                          {plan.limites?.vehiculos === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            plan.limites?.vehiculos || '50'
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Mantenimientos */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Mantenimientos/mes</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center text-white font-semibold">
                          {plan.limites?.mantenimientos === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            plan.limites?.mantenimientos || '20'
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Almacenamiento */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Almacenamiento</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center text-white font-semibold">
                          {plan.limites?.almacenamiento ? `${plan.limites.almacenamiento}GB` : '1GB'}
                        </div>
                      ))}
                    </div>
                    
                    {/* Características con checkmarks */}
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Gestión de citas</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Multi-sucursal</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_free' ? (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Reportes avanzados</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Catálogo de marcas y vehículos</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">
                        Tienda de repuestos integrada
                        <span className="text-xs text-amber-400 ml-2">(Próximamente)</span>
                      </div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">
                        Recordatorios automáticos
                        <span className="text-xs text-amber-400 ml-2">(Próximamente)</span>
                      </div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">Búsqueda por patente</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_gold' ? (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          ) : (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-6 py-4 px-6 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors">
                      <div className="font-medium text-gray-300">API completa</div>
                      {planes.map((plan) => (
                        <div key={plan.id} className="text-center">
                          {plan.id === 'revvly_gold' ? (
                            <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                          ) : (
                            <XMarkIcon className="h-6 w-6 text-gray-500 mx-auto" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Grid de planes para móvil/tablet */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:hidden gap-6">
              {planes.map((plan) => (
                <div
                  key={plan.id}
                  className={`
                    relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-200
                    ${plan.popular
                      ? 'border-purple-500 bg-purple-900/20 scale-105'
                      : 'border-gray-700 bg-gray-800 hover:border-gray-600 hover:bg-gray-750'
                    }
                    ${plan.popular ? 'ring-2 ring-purple-500 ring-opacity-50' : ''}
                  `}
                >
                  {/* Badge popular */}
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Más Popular
                      </span>
                    </div>
                  )}


                  {/* Contenido del plan */}
                  <div className="text-center">
                    {/* Logo del plan */}
                    <div className={`w-24 h-12 mx-auto mb-4 rounded-lg bg-gradient-to-r ${plan.color || 'from-blue-500 to-blue-600'} flex items-center justify-center`}>
                      <img 
                        src={revvlyLogo} 
                        alt="Revvly" 
                        className="w-20 h-8 object-contain"
                      />
                    </div>

                    {/* Nombre del plan */}
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>

                    {/* Precio */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white">
                        {selectedPeriod === 'anual' 
                          ? (plan.price === 0 ? 'Gratis' : `$${Math.round(plan.price * 12 * 0.8).toLocaleString('es-CL')}`)
                          : (plan.price === 0 ? 'Gratis' : `$${plan.price.toLocaleString('es-CL')}`)
                        }
                        {plan.price > 0 && (
                          <span className="text-lg text-gray-400">
                            {selectedPeriod === 'anual' ? '/año' : '/mes'}
                          </span>
                        )}
                      </div>
                      {selectedPeriod === 'anual' && plan.price > 0 && (
                        <div className="text-sm text-green-400">
                          Ahorra ${Math.round(plan.price * 12 * 0.2).toLocaleString('es-CL')} al año
                        </div>
                      )}
                    </div>

                    {/* Badge para plan gratuito - entre precio y descripción */}
                    {plan.price === 0 && (
                      <div className="flex justify-center mb-3">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                          Sin tarjeta de crédito
                        </div>
                      </div>
                    )}

                    {/* Descripción */}
                    <p className="text-gray-400 text-sm mb-6">{plan.description}</p>

                    {/* Límites del plan */}
                    <div className="space-y-3 text-left mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Sucursales:</span>
                        <span className="font-medium text-white">
                          {plan.limites?.sucursales === -1 ? (
                            <span className="text-green-400">Ilimitadas</span>
                          ) : (
                            <span className="text-blue-300">{plan.limites?.sucursales || '1'}</span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Clientes:</span>
                        <span className="font-medium text-white">
                          {plan.limites?.usuarios === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            <span className="text-blue-300">{plan.limites?.usuarios || '2'}</span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Vehículos:</span>
                        <span className="font-medium text-white">
                          {plan.limites?.vehiculos === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            <span className="text-blue-300">{plan.limites?.vehiculos || '50'}</span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Mantenimientos/mes:</span>
                        <span className="font-medium text-white">
                          {plan.limites?.mantenimientos === -1 ? (
                            <span className="text-green-400">Ilimitados</span>
                          ) : (
                            <span className="text-blue-300">{plan.limites?.mantenimientos || '20'}</span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Almacenamiento:</span>
                        <span className="font-medium text-blue-300">
                          {plan.limites?.almacenamiento ? `${plan.limites.almacenamiento}GB` : '1GB'}
                        </span>
                      </div>
                    </div>

                    {/* Separador */}
                    <div className="border-t border-gray-700 pt-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Características Incluidas</h4>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Gestión de citas</span>
                          <CheckIcon className="h-4 w-4 text-green-400" />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Multi-sucursal</span>
                          {plan.id === 'revvly_free' ? (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Reportes avanzados</span>
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Catálogo de marcas y vehículos</span>
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">
                            Tienda de repuestos
                            <span className="text-xs text-amber-400 ml-1">(Próximamente)</span>
                          </span>
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">
                            Recordatorios automáticos
                            <span className="text-xs text-amber-400 ml-1">(Próximamente)</span>
                          </span>
                          {plan.id === 'revvly_free' || plan.id === 'revvly_bronze' ? (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Búsqueda por patente</span>
                          {plan.id === 'revvly_gold' ? (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          ) : (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">API completa</span>
                          {plan.id === 'revvly_gold' ? (
                            <CheckIcon className="h-4 w-4 text-green-400" />
                          ) : (
                            <XMarkIcon className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de selección */}
                  <div className="mt-6">
                    <SignInButton mode="modal">
                      <button
                        type="button"
                        onClick={handleTallerLogin}
                        className={`
                          group relative w-full py-4 px-6 rounded-lg font-bold text-sm overflow-hidden transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl
                          ${plan.popular 
                            ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/40 hover:shadow-orange-500/60'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50'
                          }
                        `}
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          <span>{plan.price === 0 ? 'Comenzar Gratis' : `Prueba ${trialDays} días gratis`}</span>
                          <FaArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                        </span>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                      </button>
                    </SignInButton>
                  </div>
                </div>
              ))}
          </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 text-center">
          <div className="container mx-auto px-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 mx-auto max-w-4xl">
              <h2 className="text-4xl font-bold mb-6">¿Listo para Acelerar tu Taller?</h2>
              <p className="text-xl mb-8 opacity-90">
                Únete a los talleres que ya transformaron su gestión con Revvly
              </p>
              <div className="flex justify-center mb-6">
                <div className="bg-white/10 rounded-full px-6 py-2 text-sm font-semibold">
                  🚀 {trialDays} días gratis • Sin tarjeta de crédito • Configuración en 5 minutos
                </div>
              </div>
              <SignInButton mode="modal">
                <button 
                  onClick={handleTallerLogin}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 transform hover:scale-105"
                >
                  Empezar Ahora - Gratis
                </button>
              </SignInButton>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative py-12 border-t border-gray-800">
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black via-gray-900/80 to-transparent"></div>
          <div className="relative container mx-auto px-6 text-center">
            <div className="flex justify-center items-center mb-4">
              <img src={revvlyLogo} alt="Revvly" className="h-8 mr-2" />
              <span className="text-gray-400 text-sm">Powered by Aitomata</span>
            </div>
            <p className="text-gray-500">© 2025 Revvly. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>

      {/* Styles para animaciones personalizadas */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse-slow {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        .animate-spin-reverse-slow {
          animation: spin-reverse-slow 25s linear infinite;
        }
        @keyframes drift-1 {
          0% { transform: translate(-100px, 100vh) rotate(0deg); }
          100% { transform: translate(100vw, -100px) rotate(360deg); }
        }
        @keyframes drift-2 {
          0% { transform: translate(100vw, -100px) rotate(0deg); }
          100% { transform: translate(-100px, 100vh) rotate(-360deg); }
        }
        @keyframes drift-3 {
          0% { transform: translate(50vw, 100vh) rotate(0deg); }
          100% { transform: translate(-50vw, -100px) rotate(720deg); }
        }
        @keyframes drift-4 {
          0% { transform: translate(-100px, 50vh) rotate(0deg); }
          100% { transform: translate(100vw, -50vh) rotate(-180deg); }
        }
        @keyframes drift-5 {
          0% { transform: translate(80vw, 100vh) rotate(0deg); }
          100% { transform: translate(20vw, -100px) rotate(540deg); }
        }
        @keyframes drift-6 {
          0% { transform: translate(20vw, -100px) rotate(0deg); }
          100% { transform: translate(80vw, 100vh) rotate(-270deg); }
        }
        .animate-drift-1 {
          animation: drift-1 45s linear infinite;
        }
        .animate-drift-2 {
          animation: drift-2 50s linear infinite 5s;
        }
        .animate-drift-3 {
          animation: drift-3 40s linear infinite 10s;
        }
        .animate-drift-4 {
          animation: drift-4 55s linear infinite 15s;
        }
        .animate-drift-5 {
          animation: drift-5 48s linear infinite 20s;
        }
        .animate-drift-6 {
          animation: drift-6 42s linear infinite 25s;
        }
      `}</style>
      </div>
    </>
  );
};

export default LandingB2B;